import { collection, DocumentChange, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Collections } from "../../utils/shared/constants";
import { useDB } from "../../contexts/FirebaseContext";
import { useRestaurant } from "../../contexts/RestaurantContext";
import { useText } from "../../contexts/TextContext";
import { Order, OrderState } from "../../models/Order";
import { fromFirebaseDocs } from "../../utils/shared/firebase";

const SLIDING_WINDOW_SIZE = 4;

const compareDate = (a: Order, b: Order, date: string = 'default', desc: boolean = false) => {
    const aDate = date === 'default' ? (a.pickupDate || a.paid) : a.updated;
    const bDate = date === 'default' ? (b.pickupDate || b.paid) : b.updated;

    if (aDate && bDate) {
        const safeA = new Date(aDate);
        const safeB = new Date(bDate);

        const compare = safeA.getTime() - safeB.getTime();
        return desc ? -compare : compare;
    }
    return 0;
}


interface Result {
    ordersByState: Map<OrderState, Order[]>;
    ordersById: Map<string, Order>;
}

interface UseOrdersOptions {
    onUpcomingOrdersChange?: (changes: DocumentChange[]) => void;
    doneOrdersSlidingWindowSize?: number;
}

const useOrders = (options: UseOrdersOptions = {}): Result => {
    const [ordersByState, setOrdersByState] = useState(new Map<OrderState, Order[]>());
    const db = useDB();
    const restaurant = useRestaurant();
    const text = useText();

    const ordersById = useMemo(() => {
        const result = new Map<string, Order>();
        ordersByState.forEach((orders) => {
            orders.forEach((order) => {
                result.set(order.id || '', order);
            });
        });
        return result;
    }, [ordersByState]);

    useEffect(() => {
        const slidingWindowEnd = new Date();
        slidingWindowEnd.setHours(slidingWindowEnd.getHours() - (options.doneOrdersSlidingWindowSize || SLIDING_WINDOW_SIZE));
        const queryDoneOrders = query(
            collection(db, Collections.restaurants, restaurant.id, Collections.orders),
            where('state', '==', OrderState.DONE),
            where('updated', '>', slidingWindowEnd));
        const unsubscribeDoneOrders = onSnapshot(queryDoneOrders,
            (querySnapshot) => {
                const allOrders = fromFirebaseDocs<Order>(querySnapshot.docs);

                allOrders.sort((a, b) => compareDate(a, b, 'updated', true));

                setOrdersByState(oldOrders => {
                    const newOrders = new Map(oldOrders);
                    newOrders.set(OrderState.DONE, allOrders);
                    return newOrders;
                });
            },
            error => {
                console.error('Failed to populate dashboard with DONE orders', error);
                toast.error(text.errorDefault)
            });


        const queryUpcoming = query(
            collection(db, Collections.restaurants, restaurant.id, Collections.orders),
            where('state', 'in', [OrderState.PAID, OrderState.IN_PROGRESS]));
        const unsubscribeUpcomingOrders = onSnapshot(queryUpcoming,
            (querySnapshot) => {
                const allOrders = fromFirebaseDocs<Order>(querySnapshot.docs);

                if (options.onUpcomingOrdersChange) {
                    options.onUpcomingOrdersChange(querySnapshot.docChanges());
                }

                const newOrders = new Map<OrderState, Order[]>();
                newOrders.set(OrderState.PAID, []);
                newOrders.set(OrderState.IN_PROGRESS, []);

                for (const order of allOrders) {
                    newOrders.get(order.state)?.push(order);
                }

                newOrders.get(OrderState.PAID)?.sort(compareDate)
                newOrders.get(OrderState.IN_PROGRESS)?.sort(compareDate)

                setOrdersByState(oldOrders => {
                    newOrders.set(OrderState.DONE, oldOrders.get(OrderState.DONE) || []);
                    return newOrders;
                });
            },
            error => {
                console.error('Failed to populate dashboard', error);
                toast.error(text.errorDefault)
            });

        return () => {
            unsubscribeDoneOrders();
            unsubscribeUpcomingOrders();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, restaurant.id, text.errorDefault]);


    return {
        ordersByState,
        ordersById,
    };
}

export default useOrders;